import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/format";
import { ArrowsLeftRight } from "@phosphor-icons/react";

/**
 * Pairs an existing transaction with its other side in another account, or
 * splits a linked pair apart again. Bank statements arrive one account at a
 * time, so the two sides of a transfer are usually imported separately and
 * only linked afterwards.
 */
export default function TransferLinkPanel({
  transaction, accountsById, onFindCandidates, onLink, onUnlink, onDone,
}) {
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      onDone();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update the transfer");
    } finally {
      setBusy(false);
    }
  };

  if (transaction.transfer_id) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
        <p className="text-xs text-muted-foreground">
          Both sides of this transfer are linked. Unlink them to edit each on its own.
        </p>
        <Button
          variant="outline" size="sm" className="shrink-0 border-border" disabled={busy}
          onClick={() => run(() => onUnlink(transaction.transfer_id), "Transfer unlinked")}
          data-testid="unlink-transfer-btn"
        >
          Unlink
        </Button>
      </div>
    );
  }

  const find = async () => {
    setBusy(true);
    try {
      setCandidates(await onFindCandidates(transaction.id));
    } catch {
      toast.error("Could not look for the other side");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      {candidates === null ? (
        <Button
          variant="ghost" size="sm" className="h-auto gap-1.5 p-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground"
          disabled={busy} onClick={find} data-testid="find-transfer-btn"
        >
          <ArrowsLeftRight size={14} /> Moved between your accounts? Link it as a transfer
        </Button>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No matching {transaction.type === "expense" ? "income" : "expense"} of {eur(transaction.amount)} in
          another account within a week of this date.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Pick the other side of this transfer:</p>
          {candidates.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              disabled={busy}
              onClick={() => run(() => onLink(transaction.id, candidate.id), "Linked as a transfer")}
              data-testid={`transfer-candidate-${candidate.id}`}
              className="flex w-full items-center justify-between gap-3 rounded-md bg-secondary/50 px-3 py-2 text-left text-sm hover:bg-secondary"
            >
              <span className="min-w-0">
                <span className="block truncate">{accountsById[candidate.account_id]?.name || "Deleted account"}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {candidate.date}{candidate.description ? ` · ${candidate.description}` : ""}
                </span>
              </span>
              <span className="shrink-0 tabular-nums">{eur(candidate.amount)}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
